import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Bell, Clock3, RefreshCw, Send, Users } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import { portalAdminService } from '../../services/api';
import { PortalSessionsTab } from './PortalSessionsTab';
import { PortalCohortsTab } from './PortalCohortsTab';
import { PortalTasksTab } from './PortalTasksTab';
import { PortalAnalyticsTab } from './PortalAnalyticsTab';
import { PortalGuideTab } from './PortalGuideTab';
import { PortalTicketsTab } from './PortalTicketsTab';

const fa = (n: number) => n.toLocaleString('fa-IR');
const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500';

type TabKey =
  | 'overview'
  | 'sessions'
  | 'cohorts'
  | 'tasks'
  | 'analytics'
  | 'announce'
  | 'tickets'
  | 'guide';

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'overview', label: 'نمای کلی' },
  { key: 'sessions', label: 'جلسات و محتوا' },
  { key: 'cohorts', label: 'گروه‌ها و افراد' },
  { key: 'tasks', label: 'تسک‌ها' },
  { key: 'analytics', label: 'تحلیل' },
  { key: 'tickets', label: 'پشتیبانی' },
  { key: 'announce', label: 'اعلان' },
  { key: 'guide', label: 'راهنما' },
];

const PortalAdmin: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [program, setProgram] = useState<any>(null);
  const [overview, setOverview] = useState<any>(null);
  const [mentors, setMentors] = useState<any[]>([]);
  const [announcement, setAnnouncement] = useState({ title: '', body: '', cohortIds: [] as string[] });
  const [busy, setBusy] = useState(false);

  const flash = (message: string) => {
    setNotice(message);
    setTimeout(() => setNotice(''), 4000);
  };
  const fail = (err: any, fallback: string) => {
    setError(err?.response?.data?.message || fallback);
    setTimeout(() => setError(''), 6000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [programData, overviewData, mentorList] = await Promise.all([
        portalAdminService.getProgram(),
        portalAdminService.getOverview(),
        portalAdminService.listMentors().catch(() => []),
      ]);
      setProgram(programData);
      setOverview(overviewData);
      setMentors(mentorList);
    } catch (err: any) {
      fail(err, 'دریافت اطلاعات پرتال انجام نشد.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const reload = useCallback(async () => {
    const [programData, overviewData] = await Promise.all([
      portalAdminService.getProgram(),
      portalAdminService.getOverview(),
    ]);
    setProgram(programData);
    setOverview(overviewData);
  }, []);

  if (loading) return <LoadingSpinner />;
  if (!program) {
    return (
      <div dir="rtl" className="rounded-xl border border-gray-200 bg-white p-10 text-center">
        <p className="text-sm font-bold text-gray-500">{error || 'دوره‌ای یافت نشد.'}</p>
      </div>
    );
  }

  const totals = overview?.totals ?? {};

  /**
   * هر کارت به تبی می‌برد که کار آنجا انجام می‌شود.
   * عددی که نمی‌شود رویش کاری کرد فقط تزیین است.
   */
  const kpis: Array<{
    label: string;
    value: number;
    hint: string;
    icon: React.ReactNode;
    color: string;
    goTo?: TabKey;
  }> = [
    {
      label: 'شرکت‌کننده',
      value: totals.participants ?? 0,
      hint: fa(totals.cohorts ?? 0) + ' گروه',
      icon: <Users className="h-4 w-4" />,
      color: '#1D4ED8',
      goTo: 'cohorts',
    },
    {
      label: 'در انتظار بررسی',
      value: totals.awaitingReview ?? 0,
      hint: totals.awaitingReview ? 'منتورها باید رسیدگی کنند' : 'صف خالی است',
      icon: <Clock3 className="h-4 w-4" />,
      color: '#7C3AED',
    },
    {
      label: 'تمرین عقب‌افتاده',
      value: totals.overdue ?? 0,
      hint: totals.overdue ? 'مهلتشان گذشته' : 'همه به‌موقع',
      icon: <AlertTriangle className="h-4 w-4" />,
      color: '#d03b3b',
      goTo: 'tasks',
    },
    {
      label: 'سؤال و تیکت باز',
      value: (totals.openQuestions ?? 0) + (totals.openTickets ?? 0),
      hint:
        fa(totals.openQuestions ?? 0) + ' سؤال · ' + fa(totals.openTickets ?? 0) + ' تیکت',
      icon: <Bell className="h-4 w-4" />,
      color: '#B45309',
      goTo: 'tickets',
    },
  ];

  /**
   * فهرست کارهای زمین‌مانده. هر ردیف فقط وقتی ساخته می‌شود که واقعاً عددی پشتش باشد،
   * تا این بخش وقتی همه‌چیز مرتب است خودش را از صفحه بردارد.
   */
  const cohortsWithoutMentor = (overview?.cohorts ?? []).filter((c: any) => !c.mentor);
  const emptyCohorts = (overview?.cohorts ?? []).filter((c: any) => !c.participants);

  const attention: Array<{
    title: string;
    detail: string;
    icon: React.ReactNode;
    color: string;
    goTo?: TabKey;
  }> = [
    totals.overdue
      ? {
          title: fa(totals.overdue) + ' تمرین از مهلت گذشته',
          detail: 'شرکت‌کننده‌های عقب‌افتاده باید پیگیری شوند',
          icon: <AlertTriangle className="h-4 w-4" />,
          color: '#d03b3b',
          goTo: 'tasks' as TabKey,
        }
      : null,
    totals.openQuestions || totals.openTickets
      ? {
          title:
            fa((totals.openQuestions ?? 0) + (totals.openTickets ?? 0)) + ' پیام بی‌پاسخ',
          detail:
            fa(totals.openQuestions ?? 0) + ' سؤال و ' + fa(totals.openTickets ?? 0) + ' تیکت باز',
          icon: <Bell className="h-4 w-4" />,
          color: '#B45309',
          goTo: 'tickets' as TabKey,
        }
      : null,
    totals.awaitingReview
      ? {
          title: fa(totals.awaitingReview) + ' تحویل در انتظار بررسی',
          detail: 'منتور هنوز بازخورد نداده است',
          icon: <Clock3 className="h-4 w-4" />,
          color: '#7C3AED',
          goTo: 'tasks' as TabKey,
        }
      : null,
    cohortsWithoutMentor.length
      ? {
          title: fa(cohortsWithoutMentor.length) + ' گروه بدون منتور',
          detail: cohortsWithoutMentor.map((c: any) => c.name).join(' · '),
          icon: <Users className="h-4 w-4" />,
          color: '#1D4ED8',
          goTo: 'cohorts' as TabKey,
        }
      : null,
    totals.RED
      ? {
          title: fa(totals.RED) + ' شرکت‌کننده در وضعیت قرمز',
          detail: 'بیش از یک تمرین عقب‌افتاده یا پنج روز بی‌فعالیت',
          icon: <AlertTriangle className="h-4 w-4" />,
          color: '#d03b3b',
          goTo: 'cohorts' as TabKey,
        }
      : null,
    emptyCohorts.length
      ? {
          title: fa(emptyCohorts.length) + ' گروه خالی',
          detail: emptyCohorts.map((c: any) => c.name).join(' · '),
          icon: <Users className="h-4 w-4" />,
          color: '#64748B',
          goTo: 'cohorts' as TabKey,
        }
      : null,
  ].filter(Boolean) as Array<{
    title: string;
    detail: string;
    icon: React.ReactNode;
    color: string;
    goTo?: TabKey;
  }>;

  /** حداکثر مصرف؛ زیر یک گیگ با مگابایت نشان داده می‌شود وگرنه صفر گرد می‌شود */
  const worstCaseMb = (totals.participants ?? 0) * program.program.quotaMb;
  const worstCaseLabel =
    worstCaseMb >= 1024
      ? fa(Math.round((worstCaseMb / 1024) * 10) / 10) + ' گیگابایت'
      : fa(worstCaseMb) + ' مگابایت';

  return (
    <div dir="rtl">
      <PageHeader
        title="پرتال رشد ۶C"
        description="دورهٔ کوچینگ کسب‌وکار — جلسات، گروه‌ها، تسک‌ها و پیگیری"
        action={
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() =>
                portalAdminService
                  .runAlerts()
                  .then(() => flash('جاروی هشدارها اجرا شد.'))
                  .catch((err) => fail(err, 'اجرای هشدارها انجام نشد.'))
              }
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-bold text-gray-700"
            >
              اجرای هشدارها
            </button>
            <button
              type="button"
              onClick={load}
              className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-bold text-gray-700"
            >
              <RefreshCw className="h-4 w-4" />
              تازه‌سازی
            </button>
          </div>
        }
      />

      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
          {error}
        </div>
      ) : null}
      {notice ? (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm font-bold text-green-700">
          {notice}
        </div>
      ) : null}

      <div className="mb-4 flex gap-1 overflow-x-auto border-b border-gray-200">
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setTab(item.key)}
            className={
              'shrink-0 px-4 py-2 text-sm font-bold transition ' +
              (tab === item.key
                ? 'border-b-2 border-blue-600 text-blue-700'
                : 'text-gray-500 hover:text-gray-700')
            }
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'overview' ? (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {kpis.map((kpi) => {
              const active = kpi.value > 0;
              const body = (
                <>
                  <div className="flex items-center justify-between">
                    <span
                      className="flex h-9 w-9 items-center justify-center rounded-xl"
                      style={{
                        backgroundColor: active ? kpi.color + '14' : '#F1F5F9',
                        color: active ? kpi.color : '#94A3B8',
                      }}
                    >
                      {kpi.icon}
                    </span>
                    <span
                      className="text-3xl font-black tabular-nums"
                      style={{ color: active ? kpi.color : '#CBD5E1' }}
                    >
                      {fa(kpi.value)}
                    </span>
                  </div>
                  <p className="mt-2.5 text-[13px] font-bold text-gray-800">{kpi.label}</p>
                  <p className="mt-0.5 text-[11px] font-bold text-gray-400">{kpi.hint}</p>
                </>
              );

              return kpi.goTo ? (
                <button
                  key={kpi.label}
                  type="button"
                  onClick={() => setTab(kpi.goTo as TabKey)}
                  className="rounded-xl border border-gray-200 bg-white p-4 text-right transition hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-sm"
                >
                  {body}
                </button>
              ) : (
                <div key={kpi.label} className="rounded-xl border border-gray-200 bg-white p-4">
                  {body}
                </div>
              );
            })}
          </div>

          {attention.length ? (
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="mb-1 text-sm font-bold text-gray-800">نیازمند رسیدگی</p>
              <p className="mb-3 text-[11.5px] font-bold text-gray-500">
                فقط مواردی که همین حالا کار می‌خواهند. با پاک شدنشان این بخش هم برداشته می‌شود.
              </p>
              <div className="space-y-2">
                {attention.map((row) => (
                  <button
                    key={row.title}
                    type="button"
                    onClick={() => row.goTo && setTab(row.goTo)}
                    className="flex w-full items-center gap-3 rounded-lg border border-gray-200 p-3 text-right transition hover:border-gray-300 hover:bg-gray-50"
                  >
                    <span
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                      style={{ backgroundColor: row.color + '18', color: row.color }}
                    >
                      {row.icon}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[12.5px] font-bold text-gray-900">
                        {row.title}
                      </span>
                      <span className="block text-[11px] font-bold text-gray-500">{row.detail}</span>
                    </span>
                    {row.goTo ? (
                      <span className="shrink-0 text-[11px] font-bold text-gray-400">رسیدگی ↩</span>
                    ) : null}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-[12.5px] font-bold text-emerald-900">
                کار معوقی نیست — نه تمرین عقب‌افتاده‌ای هست، نه سؤال بی‌جواب، نه گروه بی‌منتور.
              </p>
            </div>
          )}

          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="mb-1 text-sm font-bold text-gray-800">سقف فضای فایل</p>
            <p className="mb-3 text-[11.5px] font-bold text-gray-500">
              سهمیهٔ هر نفر ضربدر تعداد شرکت‌کنندگان، حداکثر فضایی است که پرتال می‌تواند از
              دیسک سرور بگیرد. دیسک با بقیهٔ سایت مشترک است، پس عدد را با فضای آزاد بسنجید.
            </p>

            <div className="grid gap-3 sm:grid-cols-3">
              <label className="block">
                <span className="mb-1 block text-xs font-bold text-gray-600">
                  حداکثر حجم هر فایل (مگابایت)
                </span>
                <input
                  type="number"
                  defaultValue={program.program.maxFileMb}
                  onBlur={(e) =>
                    Number(e.target.value) !== program.program.maxFileMb &&
                    portalAdminService
                      .updateProgram(program.program.id, { maxFileMb: Number(e.target.value) })
                      .then(reload)
                      .then(() => flash('سقف حجم فایل تغییر کرد.'))
                      .catch((err) => fail(err, 'ذخیره انجام نشد.'))
                  }
                  className={inputClass}
                />
              </label>

              <label className="block">
                <span className="mb-1 block text-xs font-bold text-gray-600">
                  سهمیهٔ هر شرکت‌کننده (مگابایت)
                </span>
                <input
                  type="number"
                  defaultValue={program.program.quotaMb}
                  onBlur={(e) =>
                    Number(e.target.value) !== program.program.quotaMb &&
                    portalAdminService
                      .updateProgram(program.program.id, { quotaMb: Number(e.target.value) })
                      .then(reload)
                      .then(() => flash('سهمیه تغییر کرد.'))
                      .catch((err) => fail(err, 'ذخیره انجام نشد.'))
                  }
                  className={inputClass}
                />
              </label>

              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-[11px] font-bold text-amber-800">حداکثر مصرف در بدترین حالت</p>
                <p className="mt-1 text-lg font-black text-amber-900">{worstCaseLabel}</p>
                <p className="text-[10.5px] font-bold text-amber-700">
                  {fa(totals.participants ?? 0)} نفر × {fa(program.program.quotaMb)} مگابایت
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-bold text-gray-800">وضعیت گروه‌ها</p>
              <div className="flex items-center gap-3 text-[10.5px] font-bold text-gray-500">
                {[
                  { label: 'در مسیر', color: '#0ca30c' },
                  { label: 'نیازمند توجه', color: '#fab219' },
                  { label: 'عقب‌افتاده', color: '#d03b3b' },
                ].map((item) => (
                  <span key={item.label} className="inline-flex items-center gap-1">
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    {item.label}
                  </span>
                ))}
              </div>
            </div>
            <p className="mb-4 text-[11px] font-bold text-gray-400">
              نوار هر گروه ترکیب وضعیت اعضایش است؛ گروه بدون منتور با هشدار مشخص می‌شود.
            </p>

            <div className="space-y-2.5">
              {(overview?.cohorts ?? []).map((cohort: any) => {
                const total = cohort.participants || 0;
                const parts = [
                  { key: 'GREEN', label: 'در مسیر', color: '#0ca30c', value: cohort.health.GREEN },
                  { key: 'YELLOW', label: 'نیازمند توجه', color: '#fab219', value: cohort.health.YELLOW },
                  { key: 'RED', label: 'عقب‌افتاده', color: '#d03b3b', value: cohort.health.RED },
                ];
                const mentorName = cohort.mentor
                  ? [cohort.mentor.firstName, cohort.mentor.lastName].filter(Boolean).join(' ')
                  : null;

                return (
                  <div
                    key={cohort.id}
                    className="rounded-xl border border-gray-200 p-3 transition hover:border-gray-300"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[13px] font-bold text-gray-900">{cohort.name}</span>
                      {mentorName ? (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10.5px] font-bold text-gray-600">
                          {mentorName}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10.5px] font-bold text-amber-800">
                          <AlertTriangle className="h-3 w-3" />
                          بدون منتور
                        </span>
                      )}
                      <span className="mr-auto text-[11px] font-bold tabular-nums text-gray-500">
                        {fa(total)} از {fa(cohort.capacity)} نفر
                      </span>
                    </div>

                    {total ? (
                      <>
                        <div className="mt-2 flex h-2.5 gap-0.5 overflow-hidden rounded-full">
                          {parts.map((part) =>
                            part.value ? (
                              <div
                                key={part.key}
                                title={part.label + ': ' + fa(part.value)}
                                style={{
                                  width: (part.value / total) * 100 + '%',
                                  backgroundColor: part.color,
                                }}
                              />
                            ) : null,
                          )}
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-x-3 text-[10.5px] font-bold text-gray-500">
                          {parts
                            .filter((part) => part.value)
                            .map((part) => (
                              <span key={part.key} style={{ color: part.color }}>
                                {fa(part.value)} {part.label}
                              </span>
                            ))}
                        </div>
                      </>
                    ) : (
                      <p className="mt-2 rounded-lg bg-gray-50 px-3 py-2 text-[11px] font-bold text-gray-400">
                        هنوز عضوی به این گروه اضافه نشده است.
                      </p>
                    )}
                  </div>
                );
              })}

              {!(overview?.cohorts ?? []).length ? (
                <p className="py-10 text-center text-[12.5px] font-bold text-gray-400">
                  هنوز گروهی ساخته نشده. از تب «گروه‌ها و افراد» شروع کنید.
                </p>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {tab === 'sessions' ? (
        <PortalSessionsTab
          programId={program.program.id}
          sessions={program.sessions ?? []}
          onReload={reload}
          onError={fail}
          onFlash={flash}
        />
      ) : null}

      {tab === 'cohorts' ? (
        <PortalCohortsTab
          programId={program.program.id}
          cohorts={overview?.cohorts ?? program.cohorts ?? []}
          mentors={mentors}
          onReload={reload}
          onError={fail}
          onFlash={flash}
        />
      ) : null}

      {tab === 'tasks' ? (
        <PortalTasksTab
          programId={program.program.id}
          sessions={program.sessions ?? []}
          tracks={program.tracks ?? []}
          cohorts={program.cohorts ?? []}
          onError={fail}
          onFlash={flash}
        />
      ) : null}

      {tab === 'analytics' ? (
        <PortalAnalyticsTab
          programId={program.program.id}
          cohorts={program.cohorts ?? []}
          onError={fail}
        />
      ) : null}

      {tab === 'guide' ? <PortalGuideTab /> : null}

      {tab === 'tickets' ? <PortalTicketsTab /> : null}

      {tab === 'announce' ? (
        <div className="max-w-2xl rounded-xl border border-gray-200 bg-white p-4">
          <p className="mb-1 text-sm font-bold text-gray-900">اعلان عمومی یا گروهی</p>
          <p className="mb-3 text-[11.5px] font-bold text-gray-500">
            اعلان در پنل شرکت‌کننده نمایش داده می‌شود. برای یادآوری پیامکی، شماره‌ها را بعد
            از ارسال کپی کنید — متن پیامک نباید هیچ اطلاعات محرمانه‌ای داشته باشد.
          </p>

          <div className="space-y-3">
            <input
              value={announcement.title}
              onChange={(e) => setAnnouncement({ ...announcement, title: e.target.value })}
              placeholder="عنوان"
              className={inputClass}
            />
            <textarea
              rows={4}
              value={announcement.body}
              onChange={(e) => setAnnouncement({ ...announcement, body: e.target.value })}
              placeholder="متن اعلان"
              className={inputClass}
            />

            <div>
              <p className="mb-1.5 text-xs font-bold text-gray-600">
                گروه مقصد — خالی یعنی همهٔ شرکت‌کنندگان
              </p>
              <div className="flex flex-wrap gap-2">
                {(program.cohorts ?? []).map((c: any) => {
                  const active = announcement.cohortIds.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() =>
                        setAnnouncement({
                          ...announcement,
                          cohortIds: active
                            ? announcement.cohortIds.filter((x) => x !== c.id)
                            : [...announcement.cohortIds, c.id],
                        })
                      }
                      className={
                        'rounded-lg px-3 py-1.5 text-xs font-bold transition ' +
                        (active ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600')
                      }
                    >
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              type="button"
              disabled={busy || !announcement.title.trim()}
              onClick={async () => {
                setBusy(true);
                try {
                  const result = await portalAdminService.announce(program.program.id, {
                    ...announcement,
                    cohortIds: announcement.cohortIds.length ? announcement.cohortIds : undefined,
                    alsoSms: true,
                  });
                  setAnnouncement({ title: '', body: '', cohortIds: [] });
                  if (result.phones?.length) {
                    await navigator.clipboard.writeText(result.phones.join('\n')).catch(() => {});
                    flash(
                      fa(result.sent) +
                        ' اعلان ثبت شد. ' +
                        fa(result.phones.length) +
                        ' شماره برای پیامک کپی شد.',
                    );
                  } else {
                    flash(fa(result.sent) + ' اعلان ثبت شد.');
                  }
                } catch (err: any) {
                  fail(err, 'ارسال اعلان انجام نشد.');
                } finally {
                  setBusy(false);
                }
              }}
              className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              ارسال اعلان
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PortalAdmin;
